import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { DashboardLayout } from "@/components/Layout";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/lib/auth";
import {
  FileText, Users, Calendar, IndianRupee, Inbox, Cog, Eye, CheckCircle2,
  ArrowRight, TrendingUp, AlertCircle, MessageSquare, Upload,
} from "lucide-react";
import {
  ResponsiveContainer, AreaChart, Area, XAxis, YAxis, Tooltip, CartesianGrid,
} from "recharts";
import RenewalReminderBanner from "@/components/lab/RenewalReminderBanner";

export default function LabDashboard() {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [activity, setActivity] = useState([]);
  const [reports, setReports] = useState([]);
  const [lab, setLab] = useState(null);
  const [bookingRequests, setBookingRequests] = useState([]);

  useEffect(() => {
    api.get("/lab/stats").then((r) => setStats(r.data)).catch(() => {});
    api.get("/lab/activity").then((r) => setActivity(r.data.items || [])).catch(() => {});
    api.get("/reports?lite=1").then((r) => setReports(r.data)).catch(() => {});
    api.get("/lab/booking-requests?limit=8").then((r) => setBookingRequests(r.data || [])).catch(() => {});
    if (user?.lab_id) {
      api.get(`/labs/${user.lab_id}`).then((r) => setLab(r.data)).catch(() => {});
    }
  }, [user?.lab_id]);

  const today = new Date().toLocaleDateString("en-IN", { weekday: "long", day: "numeric", month: "long", year: "numeric" });
  const loading = stats === null;
  const s = stats || { reports_total: 0, reports_ready: 0, patients: 0, bookings: 0, reports_pending: 0, reports_processing: 0, reports_pending_review: 0, reports_failed: 0 };
  const awaitingReview = s.reports_pending_review || 0;
  const dash = (v) => (loading ? "—" : v);

  // Reports volume — last 14 days (from real reports)
  const chartData = useVolumeSeries(reports);

  return (
    <DashboardLayout role="lab_admin">
      <div>
        <RenewalReminderBanner />
        {lab && (lab.lifecycle_status === "pending_approval" || lab.subscription_status === "pending") && (
          <div data-testid="pending-approval-banner" className="mb-5 p-4 rounded-2xl border border-efgh-yellow bg-efgh-yellow/10 flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-efgh-orange shrink-0 mt-0.5" />
            <div className="text-sm">
              <div className="font-semibold text-ink">Awaiting subscription activation</div>
              <div className="text-ink/70 mt-0.5">
                Your lab account is created. An EFGH super admin will activate your plan after a quick verification call
                (usually within 1 business day). You can already explore the dashboard, but marketplace listing + patient
                uploads unlock once activated.
              </div>
            </div>
          </div>
        )}
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <div className="text-[11px] uppercase tracking-[0.22em] text-ink/50 font-heading font-semibold">Good morning{user?.full_name ? `, ${user.full_name.split(" ")[0]}` : ""} 👋</div>
            <h1 className="mt-0.5 font-heading font-bold text-3xl text-ink" data-testid="lab-dash-title">Dashboard</h1>
            <div className="text-sm text-ink/60">{today}</div>
          </div>
          <div className="flex gap-2">
            <Link to="/lab/reports"><Button data-testid="hdr-upload" className="bg-ink hover:bg-ink/90 text-white"><Upload className="w-4 h-4 mr-2" />Upload Report</Button></Link>
          </div>
        </div>

        {/* KPI cards */}
        <div className="mt-6 grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
          <Kpi icon={FileText} bg="bg-efgh-blue/10" color="text-efgh-blue" delta="↑ 18%" value={dash(s.reports_total)} label="Reports this month" to="/lab/reports" testid="kpi-reports" />
          <Kpi icon={Users} bg="bg-efgh-green/10" color="text-efgh-green" delta="↑ 9%" value={dash(s.patients)} label="Active patients" to="/lab/patients" testid="kpi-patients" />
          <Kpi icon={Calendar} bg="bg-efgh-orange/10" color="text-efgh-orange" delta="↑ 34%" value={dash(s.bookings)} label="Follow-up bookings" to="/lab/bookings?tab=followups" testid="kpi-followups" />
          <Kpi icon={IndianRupee} bg="bg-efgh-yellow/10" color="text-efgh-yellow" delta="↑ 22%" value={loading ? "—" : `₹${estRevenue(s.bookings)}`} label="Revenue from follow-ups" to="/lab/bookings?tab=followups" testid="kpi-revenue" />
        </div>

        {/* Report pipeline */}
        <Card className="mt-6 rounded-2xl p-6">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div>
              <div className="font-heading font-semibold text-ink text-lg">Report pipeline</div>
              <p className="text-sm text-ink/60">Live status of every report flowing through EFGH</p>
            </div>
            <Link to="/lab/reports" className="text-sm text-ink font-medium underline underline-offset-4">View all reports →</Link>
          </div>
          <div className="mt-5 grid grid-cols-2 md:grid-cols-4 gap-3">
            <Pipe icon={Inbox} color="#5A5AC7" value={s.reports_pending} label="Received" sub="via webhook/upload" />
            <Pipe icon={Cog} color="#F5C400" value={s.reports_processing} label="AI Processing" sub="avg. 42 sec" spin />
            <Pipe icon={Eye} color="#E55A25" value={awaitingReview} label="Awaiting Review" sub="flagged for staff check" />
            <Pipe icon={CheckCircle2} color="#4CAF50" value={s.reports_ready} label="Delivered" sub="SMS + WhatsApp sent" />
          </div>
        </Card>

        {/* Chart + activity */}
        <div className="mt-6 grid lg:grid-cols-[1.4fr_1fr] gap-6">
          <Card className="rounded-2xl p-6">
            <div className="flex items-center justify-between">
              <div className="font-heading font-semibold text-ink">Reports volume — last 14 days</div>
              <TrendingUp className="w-4 h-4 text-efgh-green" />
            </div>
            <div className="mt-3 h-56">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={chartData}>
                  <defs>
                    <linearGradient id="vol" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#4CAF50" stopOpacity={0.35} />
                      <stop offset="100%" stopColor="#4CAF50" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#EEF0F4" />
                  <XAxis dataKey="d" tick={{ fontSize: 10, fill: "#94a3b8" }} axisLine={false} tickLine={false} />
                  <YAxis tick={{ fontSize: 10, fill: "#94a3b8" }} axisLine={false} tickLine={false} width={26} />
                  <Tooltip contentStyle={{ borderRadius: 8, fontSize: 12 }} />
                  <Area type="monotone" dataKey="v" stroke="#4CAF50" fill="url(#vol)" strokeWidth={2.5} />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </Card>

          <Card className="rounded-2xl p-6">
            <div className="font-heading font-semibold text-ink">Recent activity</div>
            <div className="mt-4 space-y-3">
              {activity.length === 0 && <div className="text-sm text-ink/50">Activity will appear as reports flow in.</div>}
              {activity.slice(0, 8).map((a, i) => (
                <div key={i} className="flex items-start gap-3">
                  <div className={`w-8 h-8 rounded-lg grid place-items-center shrink-0 ${a.flagged ? "bg-danger-light text-danger" : a.type === "booking" ? "bg-efgh-orange/10 text-efgh-orange" : "bg-efgh-green/10 text-efgh-green"}`}>
                    {a.flagged ? <AlertCircle className="w-4 h-4" /> : a.type === "booking" ? <Calendar className="w-4 h-4" /> : <CheckCircle2 className="w-4 h-4" />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm text-ink/85 truncate">{a.message}</div>
                    <div className="text-xs text-ink/40 mt-0.5">{fmtAgo(a.at)}</div>
                  </div>
                </div>
              ))}
            </div>
          </Card>

          <Card className="rounded-2xl p-6" data-testid="dash-booking-requests">
            <div className="flex items-center justify-between">
              <div className="font-heading font-semibold text-ink">Booking requests</div>
              <Link to="/lab/bookings?tab=followups" className="text-xs text-brand hover:underline">View all →</Link>
            </div>
            {bookingRequests.length === 0 ? (
              <div className="mt-4 text-sm text-ink/50">
                No booking requests yet. Configure your booking method in <Link to="/lab/branding" className="text-brand underline">Branding → Booking</Link> so patients can Book Now from their Smart Reports.
              </div>
            ) : (
              <div className="mt-4 space-y-3">
                {bookingRequests.slice(0, 5).map((b) => (
                  <div key={b.id} className="p-3 rounded-xl border border-border flex items-start gap-3" data-testid={`br-row-${b.id}`}>
                    <div className={`w-8 h-8 rounded-lg grid place-items-center shrink-0 ${b.status === "new" ? "bg-efgh-orange/10 text-efgh-orange" : "bg-efgh-green/10 text-efgh-green"}`}>
                      <Calendar className="w-4 h-4" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium text-ink truncate">{b.patient_name} · {b.patient_phone}</div>
                      <div className="text-xs text-ink/60 truncate">{(b.tests || []).join(", ") || "—"}</div>
                      <div className="text-[11px] text-ink/40 mt-0.5">{b.collection_type} · {b.preferred_date || "any date"} · {fmtAgo(b.created_at)}</div>
                    </div>
                    <span className={`pill text-[9px] ${b.status === "new" ? "pill-borderline" : "pill-normal"}`}>{b.status}</span>
                  </div>
                ))}
              </div>
            )}
          </Card>
        </div>
      </div>
    </DashboardLayout>
  );
}

function Kpi({ icon: Icon, bg, color, delta, value, label, to, testid }) {
  const inner = (
    <Card className={`rounded-2xl p-5 ${to ? "card-hover cursor-pointer transition-transform hover:-translate-y-0.5" : ""}`} data-testid={testid}>
      <div className="flex items-start justify-between">
        <div className={`w-10 h-10 rounded-xl ${bg} ${color} grid place-items-center`}><Icon className="w-5 h-5" /></div>
        <span className="text-xs font-medium text-efgh-green">{delta}</span>
      </div>
      <div className="mt-3 font-heading font-bold text-3xl text-ink">{value}</div>
      <div className="text-xs uppercase tracking-wider text-ink/50 mt-1">{label}</div>
    </Card>
  );
  return to ? <Link to={to}>{inner}</Link> : inner;
}

function Pipe({ icon: Icon, color, value, label, sub, spin }) {
  return (
    <div className="p-4 rounded-xl border border-border">
      <div className="flex items-center justify-between">
        <div className="w-9 h-9 rounded-lg grid place-items-center text-white" style={{ backgroundColor: color }}>
          <Icon className={`w-4 h-4 ${spin ? "animate-spin" : ""}`} />
        </div>
        <div className="font-heading font-bold text-3xl text-ink">{value}</div>
      </div>
      <div className="mt-2 text-sm font-medium text-ink">{label}</div>
      <div className="text-xs text-ink/50">{sub}</div>
    </div>
  );
}

function useVolumeSeries(reports) {
  const bins = {};
  const now = new Date();
  for (let i = 13; i >= 0; i--) {
    const d = new Date(now); d.setDate(now.getDate() - i);
    bins[d.toISOString().slice(0, 10)] = 0;
  }
  for (const r of reports) {
    const k = (r.created_at || "").slice(0, 10);
    if (k in bins) bins[k]++;
  }
  return Object.entries(bins).map(([k, v]) => ({ d: k.slice(5), v }));
}

function estRevenue(bookings) {
  return ((bookings || 0) * 0.5).toFixed(1).replace(".0", "") + "K";
}

function fmtAgo(iso) {
  if (!iso) return "";
  const dt = new Date(iso);
  const s = Math.max(1, Math.floor((Date.now() - dt.getTime()) / 1000));
  if (s < 60) return `${s} sec ago`;
  if (s < 3600) return `${Math.floor(s / 60)} min ago`;
  if (s < 86400) return `${Math.floor(s / 3600)} hr ago`;
  return `${Math.floor(s / 86400)} d ago`;
}
